import { zodResolver } from '@hookform/resolvers/zod';
import { useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useForm, useController, useFormState, Controller, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import LoadingSvg from '@/components/common/LoadingSvg';
import { Button } from '@/components/ui/button';

import { useForgotPassword } from '../hooks/usePassword';
type ValidationAccountSchema = z.infer<typeof PasswordRequestSchema>;
import { PasswordRequestSchema } from '../schemas/passwordrequest.schema';

declare let grecaptcha: any;
declare let reCaptchaEnabled: any;
interface FormForgotPasswordProps {
    name?: string;
}
export const FormForgotPassword = () => {
    const mutation = useForgotPassword();
    const isMutating = useIsMutating();
    const [sentSuccess, setSentSuccess] = useState(false);
    const [sentMessage, setSentMessage] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // Check if link has expired
    const urlParams = new URLSearchParams(window.location.search);
    const isLinkExpired = urlParams.get('isvalid') === 'false';

    const {
        handleSubmit,
        register,
        control,
        setError,
        watch,
        trigger,
        setValue,
        clearErrors,
        getFieldState,
        formState: { errors, isDirty, isValid, dirtyFields, touchedFields, isSubmitting },
    } = useForm<ValidationAccountSchema>({
        resolver: zodResolver(PasswordRequestSchema),
        mode: 'all',
        criteriaMode: 'all',
        defaultValues: {
            email: '',
        },
    });
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const reCaptchaSiteKey = window.tscConfig['GoogleReCaptchaV3SiteKey'] as string;
    useEffect(() => {
        const loadReCaptcha = () => {
            const script = document.createElement('script');
            script.async = true;
            script.defer = true;
            script.src = 'https://www.google.com/recaptcha/api.js?render=' + reCaptchaSiteKey;
            document.body.appendChild(script);
        };
        loadReCaptcha();
    }, []);

    const onSubmit = (data: ValidationAccountSchema) => {
        if (hasSubmitted) return;

        setHasSubmitted(true);

        grecaptcha.ready(() => {
            grecaptcha.execute(reCaptchaSiteKey, { action: 'passwordreset' }).then((token) => {
                const payload = {
                    BaseUrl: window.location.origin,
                    Email: data.email,
                    ReCaptchaToken: token,
                };
                mutation.mutate(payload, {
                    onSuccess: (data) => {
                        setSentSuccess(true);
                        setSentMessage(data?.data?.ResponseMessage as string);
                    },
                    onError: (data) => {
                        setSentSuccess(true);
                        setSentMessage(data as string);
                        window.scrollTo({
                            top: 0,
                            behavior: 'smooth',
                        });
                    },
                    onSettled: () => {
                        setHasSubmitted(false);
                    },
                });
            });
        });
    };

    const handleClick = () => {
        // Update the state variable
        setSentSuccess(false);
    };

    // if (mutation.isError) {
    //     mutation.reset();
    //     setTimeout(() => {
    //         setError(
    //             'currentPassword',
    //             { type: 'custom', message: 'Sorry! Your password was incorrect, please try again.' },
    //             { shouldFocus: true },
    //         );
    //     }, 100);
    // }

    return (
        <>
            {/* forgot password email sent step 2 */}
            {sentSuccess && (
                <div className='className="flex flex-col"'>
                    <div className="px-5 pb-32 pt-14 md:mx-auto md:max-w-[560px]">
                        <h1 className="m-0 pb-2 text-xl font-bold text-[#1a1a1a] md:pb-4 md:text-center md:text-2xl">
                            {sentMessage.toLowerCase().includes('phone account')
                                ? 'Reset Password'
                                : 'Please check your email'}
                        </h1>
                        <p className="pb-4 text-base font-light" dangerouslySetInnerHTML={{ __html: sentMessage }}></p>
                        {/* --------- Submit --------- */}
                        <div>
                            <div className="mb-7 mt-4 grid grid-cols-1">
                                <Button
                                    onClick={handleClick}
                                    aria-label={'Resend email'}
                                    tabIndex={!isValid ? -1 : 0}
                                    disabled={sentMessage.toLowerCase().includes('phone account')}
                                >
                                    Resend email
                                </Button>
                            </div>
                        </div>
                        <div className="text-left">
                            <a className="flex justify-start no-underline" href="/pages/signin">
                                <svg
                                    className="h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                    viewBox="0 0 45 45"
                                >
                                    <g fill="none" fillRule="evenodd">
                                        <path
                                            stroke="#000"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            className="chevron"
                                            d="M26 13 16 23l10 10"
                                        />
                                    </g>
                                </svg>
                                <span className="underline">Back to Sign-in</span>
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* forgot password step 1 */}
            {!sentSuccess && (
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
                    <div className="px-5 pb-32 pt-14 md:mx-auto md:max-w-[560px]">
                        <h1
                            className={`m-0 pb-2 text-xl font-bold text-[#1a1a1a] md:pb-4 md:text-center ${isLinkExpired ? 'md:text-3xl' : 'md:text-4xl'}`}
                        >
                            {isLinkExpired ? 'Reset password link has expired' : 'Forgot your password?'}
                        </h1>
                        <p className="pb-4 text-base font-light">
                            {isLinkExpired
                                ? 'Please check your inbox for the latest email sent to reset your password.  If you haven’t received this email, please check your junk or spam folders.  Otherwise, enter your email address below and we will send you another email to reset your password.'
                                : 'Enter your email address below and we will send you an email with a link to reset your password.'}
                        </p>
                        <div className="relative z-0 mb-9">
                            <input
                                type="text"
                                className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                                placeholder=" "
                                maxLength={50}
                                data-error={getFieldState('email').error ? 'true' : 'false'}
                                // data-empty={addressWatch === '' ? 'true' : 'false'}
                                tabIndex={0}
                                {...register('email')}
                                data-qm-encrypt="true"
                            />
                            <label
                                className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-[30px] scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:-translate-y-2.5 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-[30px] peer-focus:scale-75"
                                htmlFor="email"
                            >
                                Email address *
                            </label>
                            {errors.email && <div className="mt-1 text-xs text-[#df0c0c]">{errors.email.message}</div>}
                        </div>
                        {/* --------- Submit --------- */}
                        <div>
                            <div className="mb-7 mt-4 grid grid-cols-1">
                                <Button
                                    type="submit"
                                    onClick={(e) => {
                                        if (hasSubmitted) {
                                            e.preventDefault();
                                        }
                                    }}
                                    disabled={!isValid || isSubmitting || hasSubmitted}
                                >
                                    Send email
                                </Button>
                            </div>
                        </div>
                        <div className="text-left">
                            <a className="flex justify-start no-underline" href="/pages/signin">
                                <svg
                                    className="h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                    viewBox="0 0 45 45"
                                >
                                    <g fill="none" fillRule="evenodd">
                                        <path
                                            stroke="#000"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            className="chevron"
                                            d="M26 13 16 23l10 10"
                                        />
                                    </g>
                                </svg>
                                <span className="underline">Back to sign-in</span>
                            </a>
                        </div>
                    </div>
                    {mutation.isLoading || isMutating ? (
                        <div className="blockPageWrap" style={{ display: 'block' }}>
                            <div className="blockPageOverlay"></div>
                            <div className="blockMsg">
                                <LoadingSvg />
                            </div>
                        </div>
                    ) : null}
                </form>
            )}
        </>
    );
};
